import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import PrimaryBtn from "../button/PrimaryBtn";

interface Lifeline {
  lifeline_id: {
    name: { en: string; ar: string };
    description: { en: string; ar: string };
    image: string;
    _id: string;
  };
  is_used: boolean;
  _id: string;
}

interface LifelineSuccessModalProps {
  isOpen: boolean;
  lifeline: Lifeline | null;
  teamName: string;
  onClose: () => void;
  autoCloseDelay?: number;
  customMessage?: string;
}

const LifelineSuccessModal = ({
  isOpen,
  lifeline,
  teamName,
  onClose,
  autoCloseDelay = 3000,
  customMessage,
}: LifelineSuccessModalProps) => {
  const currentLanguage = (localStorage.getItem("i18nextLng") || "en") as "en" | "ar";
  const baseUrl = import.meta.env.VITE_BASE_CDN_URL;
  const { t } = useTranslation();

  // Auto close after delay
  //   useEffect(() => {
  //     if (isOpen && autoCloseDelay > 0) {
  //       const timer = setTimeout(() => {
  //         onClose();
  //       }, autoCloseDelay);
  //       return () => clearTimeout(timer);
  //     }
  //   }, [isOpen, autoCloseDelay, onClose]);

  if (!isOpen || !lifeline) return null;

  const lifelineData = lifeline?.lifeline_id;
  const lifelineName = lifelineData?.name?.[currentLanguage] || lifelineData?.name?.en || "Lifeline";
  const lifelineImage = lifelineData?.image ? `${baseUrl}/${lifelineData.image}` : "";

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-5 z-50">
      {/* Confetti/Celebration Background */}
      {/* <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-3 h-3 rounded-full animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-10%`,
              backgroundColor: ["#7364FF", "#C3DC78", "#FFD700", "#FF6B6B", "#4ECDC4"][i % 5],
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
            }}
          />
        ))}
      </div> */}

      <div className="modal bg-white-500 2xl:rounded-[2.083vw] xl:rounded-[2.083vw] lg:rounded-[2.5vw] rounded-3xl 2xl:p-[2vw] xl:p-[2vw] lg:p-[2.5vw] md:p-7 p-4 2xl:max-w-[30vw] xl:max-w-[32vw] lg:max-w-[38vw] sm:max-w-[60vw] max-w-[90vw] 2xl:shadow-[0px_0px_3.854vw_0px_#C3DC78CC_inset] shadow-[0px_0px_3.854vw_0px_#C3DC78CC_inset] w-full relative animate-[celebrationBounce_0.6s_ease-out] 2xl:border-[0.2vw] lg:border-[0.25vw] border-3 border-primary auth-page overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)] hide-scroll">
        {/* Success Glow Effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-[#C3DC78]/10 pointer-events-none" />

        {/* Success Icon */}
        <div className="relative flex justify-center 2xl:mb-[1.2vw] mb-5">
          <div className="relative">
            {/* Pulse rings */}
            <div className="absolute inset-0 2xl:w-[6vw] 2xl:h-[6vw] xl:w-24 w-20 xl:h-24 h-20 rounded-full bg-primary/20 animate-ping" />
            <div className="absolute inset-0 2xl:w-[6vw] 2xl:h-[6vw] xl:w-24 w-20 xl:h-24 h-20 rounded-full bg-primary/30 animate-pulse" />

            {/* Success checkmark circle */}
            <div className="relative 2xl:w-[6vw] 2xl:h-[6vw] xl:w-24 w-20 xl:h-24 h-20 rounded-full bg-gradient-to-br from-primary to-[#453C99] flex items-center justify-center shadow-lg shadow-primary/40">
              <svg
                className="2xl:w-[3vw] 2xl:h-[3vw] xl:w-12 w-10 xl:h-12 h-10 text-white animate-[checkmark_0.5s_ease-out_0.3s_both]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </div>
        </div>

        {/* Message */}
        <p className="text-center text-gray-600 2xl:text-[1vw]/snug xl:text-lg/snug text-base/snug 2xl:mb-[1.2vw] mb-5 animate-[fadeInUp_0.4s_ease-out_0.3s_both]">
          {customMessage || t("lifelineSuccessModal.lifelineUsedSuccessfully")}
        </p>

        {/* Lifeline Card */}
        <div className="relative bg-gradient-to-br from-white to-gray-50 2xl:rounded-[1.2vw] rounded-2xl 2xl:p-[1.2vw] p-4 2xl:mb-[1.2vw] mb-5 border-2 border-primary/20 shadow-inner animate-[fadeInUp_0.4s_ease-out_0.4s_both]">
          <div className="flex items-center 2xl:gap-[1vw]  gap-4">
            {/* Lifeline Image */}

            {/* Lifeline Info */}
            <div className="flex-1 min-w-0 justify-center items-center flex flex-col">
              {lifelineImage && (
                <div className="2xl:w-[4vw] 2xl:h-[4vw] xl:w-16 w-14 xl:h-16 h-14">
                  <img src={lifelineImage} alt={lifelineName} className="w-full h-full object-contain drop-shadow-lg" />
                </div>
              )}
              <h2 className="2xl:text-[1.2vw]/tight xl:text-xl/tight text-lg/tight font-bold text-black truncate font-garamond">
                {lifelineName}
              </h2>
              <div className="flex items-center 2xl:gap-[0.4vw] gap-1.5 2xl:mt-[0.3vw] mt-1">
                <span className="2xl:text-[0.7vw] text-sm text-black-300">{t("lifelineSuccessModal.usedBy")}</span>
                <span className="inline-block 2xl:px-[0.833vw] px-3 2xl:py-[0.25vw] py-1 bg-primary-ivory lg:border-[0.13vw] border-2 border-transparent rounded-full text-black-300 2xl:text-[0.729vw] text-xs font-medium">
                  {teamName}
                </span>
              </div>
            </div>
          </div>

          {/* Sparkle decorations */}
          <div className="absolute -top-2 -right-2 2xl:w-[1.5vw] 2xl:h-[1.5vw] w-6 h-6 text-yellow-400 animate-spin-slow">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
            </svg>
          </div>
          <div className="absolute -bottom-1 -left-1 2xl:w-[1vw] 2xl:h-[1vw] w-4 h-4 text-primary animate-pulse">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
            </svg>
          </div>
        </div>

        {/* Continue Button */}
        <div className="flex justify-center items-center">
          <PrimaryBtn onClick={onClose} className="w-full">
            {t("lifelineSuccessModal.continue")}
          </PrimaryBtn>
        </div>
      </div>

      <style>{`
        @keyframes celebrationBounce {
          0% {
            opacity: 0;
            transform: scale(0.3) rotate(-5deg);
          }
          50% {
            transform: scale(1.08) rotate(2deg);
          }
          70% {
            transform: scale(0.95) rotate(-1deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }

        @keyframes checkmark {
          0% {
            stroke-dasharray: 100;
            stroke-dashoffset: 100;
          }
          100% {
            stroke-dasharray: 100;
            stroke-dashoffset: 0;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }

        .animate-confetti {
          animation: confetti linear forwards;
        }

        .animate-spin-slow {
          animation: spin 3s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default LifelineSuccessModal;
